import React, { useRef, useEffect, useCallback, useState } from 'react';
import { IRecaptchaProps } from '../../../types/humanVerification';
import { useHumanVerification } from '../../../hooks/useHumanVerification';
import { trackExceptionWithContext } from '../../../services/application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from '../../../services/application-insights/appInsightsConstants';

declare global {
	interface Window {
		grecaptcha?: {
			ready: (callback: () => void) => void;
			render: (
				container: HTMLElement | string,
				options: {
					sitekey: string;
					callback?: (token: string) => void;
					'expired-callback'?: () => void;
					'error-callback'?: (error: string) => void;
					theme?: 'light' | 'dark';
					size?: 'normal' | 'compact' | 'flexible' | 'invisible';
					tabindex?: number;
				},
			) => number;
			execute: (widgetId?: number) => Promise<string>;
			reset: (widgetId?: number) => void;
			getResponse: (widgetId?: number) => string;
		};
	}
}

export const RecaptchaWidget: React.FC<IRecaptchaProps> = ({
	siteKey,
	version = 'v2',
	action,
	minScore,
	size = 'normal',
	tabIndex,
	onVerified,
	onError,
	onExpired,
	onLoad,
	className,
	...props
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<number | null>(null);
	const scriptLoadedRef = useRef(false);
	const [isReady, setIsReady] = useState(false);

	const { verify, isLoading } = useHumanVerification({
		provider: 'recaptcha',
		action,
		minScore,
		onVerified,
		onError,
	});

	const handleVerification = useCallback(
		async (token: string) => {
			if (!token) {
				const errorMessage = 'No token received from reCAPTCHA';
				trackExceptionWithContext(new Error(errorMessage), {
					component: AppInsightsComponents.RECAPTCHA_WIDGET,
					action: 'verification-no-token',
					additionalData: {
						errorType: AppInsightsErrorTypes.RECAPTCHA_VERIFICATION_ERROR,
						version,
						action,
						siteKey: siteKey?.substring(0, 10) + '...'
					}
				});
				onError?.(errorMessage);
				return;
			}

			try {
				const result = await verify(token);

				if (result.isSuccess) {
					onVerified?.(token, result);
				} else {
					const errorMessage = result.errorMessage || 'Verification failed';
					trackExceptionWithContext(new Error(errorMessage), {
						component: AppInsightsComponents.RECAPTCHA_WIDGET,
						action: 'verification-failed',
						additionalData: {
							errorType: AppInsightsErrorTypes.RECAPTCHA_VERIFICATION_ERROR,
							version,
							action,
							hasToken: !!token,
							tokenLength: token?.length || 0,
							siteKey: siteKey?.substring(0, 10) + '...'
						}
					});
					onError?.(errorMessage);
				}
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.RECAPTCHA_WIDGET,
					action: 'verification-exception',
					additionalData: {
						errorType: AppInsightsErrorTypes.RECAPTCHA_VERIFICATION_ERROR,
						version,
						action,
						hasToken: !!token,
						siteKey: siteKey?.substring(0, 10) + '...'
					}
				});
				onError?.(error instanceof Error ? error.message : 'Verification failed');
			}
		},
		[verify, onVerified, onError, version, action, siteKey],
	);

	const handleExpired = useCallback(() => {
		// console.log('reCAPTCHA token expired');
		onExpired?.();
	}, [onExpired]);

	const handleError = useCallback(
		(error: string) => {
			console.error('reCAPTCHA error:', error);
			trackExceptionWithContext(new Error(error), {
				component: AppInsightsComponents.RECAPTCHA_WIDGET,
				action: 'widget-error',
				additionalData: {
					errorType: AppInsightsErrorTypes.RECAPTCHA_WIDGET_ERROR,
					version,
					action,
					siteKey: siteKey?.substring(0, 10) + '...'
				}
			});
			onError?.(error);
		},
		[onError, version, action, siteKey],
	);

	const loadRecaptchaScript = useCallback(() => {
		if (scriptLoadedRef.current || document.querySelector('script[src*="recaptcha"]')) {
			return Promise.resolve();
		}

		return new Promise<void>((resolve, reject) => {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${version === 'v3' ? siteKey : 'explicit'}`;
			script.async = true;
			script.defer = true;

			script.onload = () => {
				scriptLoadedRef.current = true;
				resolve();
			};

			script.onerror = () => {
				const error = new Error('Failed to load reCAPTCHA script');
				trackExceptionWithContext(error, {
					component: AppInsightsComponents.RECAPTCHA_WIDGET,
					action: 'script-load-failed',
					additionalData: {
						errorType: AppInsightsErrorTypes.RECAPTCHA_SCRIPT_LOAD_ERROR,
						scriptSrc: script.src,
						version,
						action,
						siteKey: siteKey?.substring(0, 10) + '...'
					}
				});
				reject(error);
			};

			document.head.appendChild(script);
		});
	}, [siteKey, version]);

	const renderV2Widget = useCallback(() => {
		if (!containerRef.current || !window.grecaptcha || !isReady) {
			return;
		}

		try {
			widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
				sitekey: siteKey,
				callback: handleVerification,
				'expired-callback': handleExpired,
				'error-callback': handleError,
				size,
				tabindex: tabIndex,
			});
		} catch (error) {
			console.error('Failed to render reCAPTCHA v2 widget:', error);
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.RECAPTCHA_WIDGET,
				action: 'render-v2-failed',
				additionalData: {
					errorType: AppInsightsErrorTypes.RECAPTCHA_RENDER_ERROR,
					version,
					siteKey: siteKey?.substring(0, 10) + '...',
					size,
					tabIndex
				}
			});
			onError?.(error instanceof Error ? error.message : 'Failed to render widget');
		}
	}, [siteKey, handleVerification, handleExpired, handleError, size, tabIndex, isReady, onError]);

	const executeV3 = useCallback(async () => {
		if (!window.grecaptcha || !isReady || !action) {
			return;
		}

		try {
			const token = await (window.grecaptcha as any).execute(siteKey, { action });
			await handleVerification(token);
		} catch (error) {
			console.error('Failed to execute reCAPTCHA v3:', error);
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.RECAPTCHA_WIDGET,
				action: 'execute-v3-failed',
				additionalData: {
					errorType: AppInsightsErrorTypes.RECAPTCHA_EXECUTION_ERROR,
					version,
					action,
					siteKey: siteKey?.substring(0, 10) + '...',
					isReady
				}
			});
			onError?.(error instanceof Error ? error.message : 'Failed to execute reCAPTCHA');
		}
	}, [siteKey, action, handleVerification, isReady, onError]);

	const executeWidget = useCallback(async () => {
		if (version === 'v3') {
			await executeV3();
		} else if (window.grecaptcha && widgetIdRef.current !== null) {
			try {
				const token = await window.grecaptcha.execute(widgetIdRef.current);
				await handleVerification(token);
			} catch (error) {
				console.error('Failed to execute reCAPTCHA widget:', error);
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.RECAPTCHA_WIDGET,
					action: 'execute-widget-failed',
					additionalData: {
						errorType: AppInsightsErrorTypes.RECAPTCHA_EXECUTION_ERROR,
						version,
						widgetId: widgetIdRef.current,
						siteKey: siteKey?.substring(0, 10) + '...'
					}
				});
				onError?.(error instanceof Error ? error.message : 'Failed to execute widget');
			}
		}
	}, [version, executeV3, handleVerification, onError]);

	// const resetWidget = useCallback(() => {
	// 	if (window.grecaptcha && widgetIdRef.current !== null) {
	// 		window.grecaptcha.reset(widgetIdRef.current);
	// 	}
	// }, []);

	useEffect(() => {
		const initializeWidget = async () => {
			try {
				await loadRecaptchaScript();

				if (window.grecaptcha) {
					window.grecaptcha.ready(() => {
						setIsReady(true);
						onLoad?.();

						if (version === 'v2') {
							renderV2Widget();
						}
					});
				}
			} catch (error) {
				console.error('Failed to initialize reCAPTCHA:', error);
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.RECAPTCHA_WIDGET,
					action: 'initialization-failed',
					additionalData: {
						errorType: AppInsightsErrorTypes.RECAPTCHA_INITIALIZATION_ERROR,
						version,
						siteKey: siteKey?.substring(0, 10) + '...',
						hasGrecaptcha: !!window.grecaptcha
					}
				});
				onError?.(error instanceof Error ? error.message : 'Failed to initialize widget');
			}
		};

		initializeWidget();
	}, [loadRecaptchaScript, renderV2Widget, onLoad, onError, version]);

	// Auto-execute v3 on mount
	useEffect(() => {
		if (version === 'v3' && isReady) {
			executeV3();
		}
	}, [version, isReady, executeV3]);

	const containerClasses = [
		'recaptcha-widget',
		version === 'v3' ? 'recaptcha-v3' : 'recaptcha-v2',
		isLoading ? 'opacity-50' : '',
		className || '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={containerClasses} {...props}>
			{version === 'v2' && <div ref={containerRef} className="recaptcha-container" />}

			{version === 'v3' && (
				<div className="recaptcha-v3-info">
					<p className="text-xs text-gray-500">
						This site is protected by reCAPTCHA and the Google{' '}
						<a
							href="https://policies.google.com/privacy"
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-500 hover:underline"
						>
							Privacy Policy
						</a>{' '}
						and{' '}
						<a
							href="https://policies.google.com/terms"
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-500 hover:underline"
						>
							Terms of Service
						</a>{' '}
						apply.
					</p>
				</div>
			)}

			{isLoading && (
				<div className="absolute inset-0 flex items-center justify-center bg-white/50">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
				</div>
			)}

			{size === 'invisible' && (
				<button
					type="button"
					onClick={executeWidget}
					className="recaptcha-execute-btn btn btn-primary"
					disabled={!isReady || isLoading}
				>
					Verify
				</button>
			)}
		</div>
	);
};

export default RecaptchaWidget;
